import { z } from 'zod';

export const companionshipEnum = z.enum([
  'solo',
  'couple',
  'family',
  'friends',
  'relationship',
]);

export const transportModes = [
  'plane',
  'train',
  'bus',
  'car',
  'boat',
  'bike',
  'walk',
];

export const countryCitySchema = z.object({
  country: z.string().min(1, 'Country is required'),
  cities: z.array(z.string().min(1)).default([]),
});

export const tripCreatorSchema = z
  .object({
    durationDays: z
      .preprocess((v) => (v === '' || v === null || v === undefined ? undefined : Number(v)), z
        .number({ invalid_type_error: 'Duration must be a number' })
        .int('Duration must be an integer')
        .positive('Duration must be positive')
        .max(365, 'Duration too long')
        .optional()
      ),
    budget: z
      .preprocess((v) => (v === '' || v === null || v === undefined ? undefined : Number(v)), z
        .number({ invalid_type_error: 'Budget must be a number' })
        .positive('Budget must be positive')
        .max(1000000, 'Budget too high')
        .optional()
      ),
    startDate: z
      .preprocess((v) => (v ? new Date(v) : undefined), z.date().optional()),
    companionship: z.preprocess((v) => (v === '' ? undefined : v), companionshipEnum.optional()),
  // Deprecated: original combined structure
  locations: z.array(countryCitySchema).default([]),
  // New flat selections
  countries: z.array(z.object({ id: z.any(), label: z.string().min(1) })).default([]),
  cities: z.array(z.object({ id: z.any(), label: z.string().min(1), type: z.string().optional() })).default([]),
    // Places/users are selected via search; expect objects with id + label
    mustVisit: z.array(z.object({ id: z.any(), label: z.string().min(1) })).default([]),
    excludePlaces: z.array(z.object({ id: z.any(), label: z.string().min(1) })).default([]),
    transport: z.array(z.enum(transportModes)).default([]),
    minSleepHours: z
      .preprocess((v) => (v === '' || v === null || v === undefined ? undefined : Number(v)), z
        .number({ invalid_type_error: 'Min sleep must be a number' })
        .int('Min sleep must be an integer')
        .min(0, 'Min sleep cannot be negative')
        .max(14, 'Min sleep is unrealistically high')
        .optional()
      ),
    companions: z
      .array(z.object({ id: z.any(), label: z.string().min(1), avatar: z.string().optional() }))
      .max(20, 'You can add up to 20 companions')
      .default([]),
  })
  .refine((data) => Boolean(data.durationDays) || Boolean(data.budget), {
    message: 'Provide either duration or budget',
    path: ['durationDays'],
  });

export function softValidateLocationsAgainstDuration(locations, durationDays) {
  if (!durationDays || !Array.isArray(locations)) return null;
  // Support both old (locations) and new (countries + cities) until migration complete
  let totalCountries = 0;
  let totalCities = 0;
  if (Array.isArray(locations) && locations.length) {
    totalCountries = locations.length;
    totalCities = locations.reduce((acc, l) => acc + (l.cities?.length || 0), 0);
  }
  // If new fields are present use them
  if (Array.isArray(locations) && locations.length === 0) {
    // Expect caller to pass countries & cities separately (outside this helper) if needed
  }

  // Soft caps: roughly 1-2 cities/day and ≤ duration/2 countries
  const maxCities = Math.max(3, durationDays * 2);
  const maxCountries = Math.max(1, Math.ceil(durationDays / 2));

  const warnings = [];
  if (totalCities > maxCities) {
    warnings.push(`You selected ${totalCities} cities for ${durationDays} days. Consider ≤ ${maxCities}.`);
  }
  if (totalCountries > maxCountries) {
    warnings.push(`You selected ${totalCountries} countries for ${durationDays} days. Consider ≤ ${maxCountries}.`);
  }
  return warnings.length ? warnings : null;
}
